import { useEffect, useState } from "react";
import { X, Loader2, Pill, Sparkles, Search } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

/**
 * MedicationDialog — Add/Edit medication with catalog autocomplete.
 * One-tap prefill (spec §67): typing 2+ chars queries /vault/health/medications/catalog
 * and shows top 6 matches; tapping one autofills name, generic, brand, strength picker, frequency.
 */
export default function MedicationDialog({ initial, onClose, onSaved }) {
  const [form, setForm] = useState(() => ({
    name: initial?.name || "",
    generic: initial?.generic || "",
    brand: initial?.brand || "",
    strength: initial?.strength || "",
    dose: initial?.dose || "",
    frequency: initial?.frequency || "",
    times: (initial?.times || []).join(", "),
    start_date: initial?.start_date || "",
    end_date: initial?.end_date || "",
    physician: initial?.physician || "",
    pharmacy: initial?.pharmacy || "",
    refills_remaining: initial?.refills_remaining ?? "",
    notes: initial?.notes || "",
    is_active: initial?.is_active ?? true,
  }));
  const [suggest, setSuggest] = useState([]);
  const [strengthOptions, setStrengthOptions] = useState([]);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Autocomplete against catalog
  useEffect(() => {
    if (initial) { setSuggest([]); return; }
    const q = form.name.trim();
    if (q.length < 2) { setSuggest([]); return; }
    let cancelled = false;
    api.get("/vault/health/medications/catalog", { params: { q, limit: 6 } })
      .then((r) => { if (!cancelled) setSuggest(r.data.items || []); })
      .catch(() => { if (!cancelled) setSuggest([]); });
    return () => { cancelled = true; };
  }, [form.name, initial]);

  const applyCatalog = (row) => {
    setForm((f) => ({
      ...f,
      name: row.generic,
      generic: row.generic,
      brand: row.brand,
      strength: (row.strengths && row.strengths[0]) || "",
      frequency: row.typical_frequency || f.frequency,
    }));
    setStrengthOptions(row.strengths || []);
    setSuggest([]);
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Medication name is required");
    setBusy(true);
    try {
      const body = {
        name: form.name.trim(),
        generic: form.generic || null,
        brand: form.brand || null,
        strength: form.strength || null,
        dose: form.dose || null,
        frequency: form.frequency || null,
        times: (form.times || "").split(",").map((s) => s.trim()).filter(Boolean),
        start_date: form.start_date || null,
        end_date: form.end_date || null,
        physician: form.physician || null,
        pharmacy: form.pharmacy || null,
        refills_remaining: form.refills_remaining === "" ? null : Number(form.refills_remaining),
        notes: form.notes || null,
        is_active: !!form.is_active,
      };
      if (initial) {
        await api.put(`/vault/health/medications/${initial.id}`, body);
        toast.success("Medication updated");
      } else {
        await api.post("/vault/health/medications", body);
        toast.success("Medication added");
      }
      onSaved && onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save medication");
    }
    setBusy(false);
  };

  const inp = "mt-1 w-full border border-zinc-300 px-3 py-2 text-sm";

  return (
    <div className="fixed inset-0 z-[9998] bg-zinc-900/60 backdrop-blur-sm flex items-end sm:items-center justify-center"
         onClick={onClose} data-testid="medication-dialog">
      <div className="bg-white border border-zinc-200 shadow-2xl w-full sm:max-w-lg max-h-[92vh] overflow-y-auto sm:rounded-lg" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-zinc-200 sticky top-0 bg-white z-10">
          <div className="flex items-center gap-2">
            <Pill className="w-5 h-5 text-emerald-600" />
            <h3 className="font-heading font-800 text-lg">{initial ? "Edit medication" : "Add medication"}</h3>
          </div>
          <button onClick={onClose} data-testid="medication-close" className="text-zinc-500 hover:text-destructive"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-4 space-y-3">
          <label className="block text-sm">
            <span className="font-600 flex items-center gap-1"><Search className="w-3.5 h-3.5 text-zinc-400" /> Name (search or type)</span>
            <input autoFocus value={form.name} onChange={(e) => set("name", e.target.value)}
              data-testid="medication-name" placeholder="e.g. Lisinopril, Metformin"
              className={inp} autoComplete="off" />
          </label>

          {suggest.length > 0 && !initial && (
            <div className="border border-emerald-200 bg-emerald-50/50 p-2 space-y-1" data-testid="medication-suggestions">
              <div className="text-[10px] font-800 uppercase tracking-wider text-emerald-800 flex items-center gap-1"><Sparkles className="w-3 h-3" /> Common medications</div>
              {suggest.map((s) => (
                <button key={s.id} type="button" onClick={() => applyCatalog(s)}
                  data-testid={`medication-suggestion-${s.id}`}
                  className="w-full text-left px-2 py-1.5 hover:bg-white text-sm">
                  <div className="font-700 text-zinc-900">{s.generic} <span className="font-400 text-zinc-500">· {s.brand}</span></div>
                  <div className="text-[11px] text-muted-foreground">{s.strengths.join(" · ")} · {s.typical_frequency}</div>
                </button>
              ))}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <label className="block text-sm">
              <span className="font-600">Strength</span>
              {strengthOptions.length > 0 ? (
                <select value={form.strength} onChange={(e) => set("strength", e.target.value)} data-testid="medication-strength" className={inp}>
                  {strengthOptions.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              ) : (
                <input value={form.strength} onChange={(e) => set("strength", e.target.value)} data-testid="medication-strength" placeholder="e.g. 20 mg" className={inp} />
              )}
            </label>
            <label className="block text-sm">
              <span className="font-600">Dose</span>
              <input value={form.dose} onChange={(e) => set("dose", e.target.value)} data-testid="medication-dose" placeholder="1 tablet" className={inp} />
            </label>
          </div>

          <label className="block text-sm">
            <span className="font-600">Frequency</span>
            <input value={form.frequency} onChange={(e) => set("frequency", e.target.value)} data-testid="medication-frequency" placeholder="Once daily" className={inp} />
          </label>

          <label className="block text-sm">
            <span className="font-600">Times <span className="font-400 text-muted-foreground">(24h, comma-separated)</span></span>
            <input value={form.times} onChange={(e) => set("times", e.target.value)} data-testid="medication-times" placeholder="08:00, 20:00" className={inp} />
          </label>

          <details className="text-sm">
            <summary className="cursor-pointer font-600 text-zinc-700">More details (optional)</summary>
            <div className="mt-3 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <label className="block text-sm"><span className="font-600">Start date</span>
                  <input type="date" value={form.start_date} onChange={(e) => set("start_date", e.target.value)} data-testid="medication-start" className={inp} />
                </label>
                <label className="block text-sm"><span className="font-600">End date</span>
                  <input type="date" value={form.end_date} onChange={(e) => set("end_date", e.target.value)} data-testid="medication-end" className={inp} />
                </label>
              </div>
              <label className="block text-sm"><span className="font-600">Prescribing physician</span>
                <input value={form.physician} onChange={(e) => set("physician", e.target.value)} data-testid="medication-physician" className={inp} />
              </label>
              <label className="block text-sm"><span className="font-600">Pharmacy</span>
                <input value={form.pharmacy} onChange={(e) => set("pharmacy", e.target.value)} data-testid="medication-pharmacy" className={inp} />
              </label>
              <label className="block text-sm"><span className="font-600">Refills remaining</span>
                <input type="number" min="0" value={form.refills_remaining} onChange={(e) => set("refills_remaining", e.target.value)} data-testid="medication-refills" className={inp} />
              </label>
              <label className="block text-sm"><span className="font-600">Notes</span>
                <textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="medication-notes" className={inp} maxLength={500} />
              </label>
            </div>
          </details>

          <p className="text-[11px] text-muted-foreground leading-snug">
            My Health Vault does not modify a doctor-prescribed schedule automatically, and does not replace medical advice.
          </p>
        </div>

        <div className="flex items-center justify-end gap-2 p-4 border-t border-zinc-200 sticky bottom-0 bg-white">
          <button onClick={onClose} disabled={busy} className="px-4 py-2 border border-zinc-300 text-sm">Cancel</button>
          <button onClick={save} disabled={busy} data-testid="medication-save"
            className="px-4 py-2 bg-emerald-600 text-white text-sm font-700 disabled:opacity-60 inline-flex items-center gap-1.5">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} {initial ? "Save changes" : "Add medication"}
          </button>
        </div>
      </div>
    </div>
  );
}
